import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useReorderSensors } from "@/hooks/useReorderSensors";
import { reorderByDragEnd } from "@/lib/reorder";
import { Plus, UtensilsCrossed, Sparkles, type LucideIcon } from "lucide-react";
import { Button, Chip, ConfirmModal } from "@/components/ui";
import { LoadingState, SectionCard } from "@/components/common";
import {
  ProductItemModal,
  type ProductItem,
  type ProductItemCreate,
  type ProductItemUpdate,
} from "@/components/common/ProductItemModal";
import { SortableItem } from "@/components/common/SortableItem";
import { EmptyState } from "@/components/ui";
import type { EntityType } from "@/services/storageService";
import { MENU_CATEGORIES, menuCategoryDisplayLabel } from "@/constants";
import type { MenuCategory, ServiceCategory } from "@/types";
import { resolveCatalogTranslation } from "@/lib/translationResolution";

export interface ProductItemsSectionProps {
  activityId: string;
  /** Item type - menu or service */
  itemType: "menu" | "service";
  /** Entity type for storage */
  entityType: EntityType;
  /** Query key for the items list */
  queryKey: string;
  /** Service functions */
  getAllFn: (activityId: string) => Promise<ProductItem[]>;
  createFn: (
    activityId: string,
    data: ProductItemCreate,
  ) => Promise<ProductItem>;
  updateFn: (
    activityId: string,
    itemId: string,
    data: ProductItemUpdate,
  ) => Promise<ProductItem>;
  deleteFn: (activityId: string, itemId: string) => Promise<void>;
  serviceCategories?: ServiceCategory[];
  activityCommissionRate: number;
}

export function ProductItemsSection({
  activityId,
  itemType,
  entityType,
  queryKey,
  getAllFn,
  createFn,
  updateFn,
  deleteFn,
  serviceCategories = [],
  activityCommissionRate,
}: ProductItemsSectionProps) {
  const { t, i18n } = useTranslation("components");
  const { t: tCommon } = useTranslation("common");
  const queryClientHook = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProductItem | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ProductItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("ALL");

  const isMenu = itemType === "menu";
  const sortedServiceCategories = useMemo(
    () =>
      [...serviceCategories].sort((a, b) => a.display_order - b.display_order),
    [serviceCategories],
  );
  // Restaurants always keep the fixed menu courses. Custom labels in
  // service_categories are extra groups, not a replacement.
  const groupsByServiceCategory = !isMenu;

  const sensors = useReorderSensors();

  const theme = useMemo(
    () => ({
      itemBgColor: "border-surface-200 bg-surface-0 hover:border-surface-300",
      priceColor: "text-surface-700",
      buttonVariant: "primary" as const,
      Icon: (isMenu ? UtensilsCrossed : Sparkles) as LucideIcon,
      title: isMenu
        ? t("productItems.menu.title")
        : t("productItems.service.title"),
      emptyTitle: isMenu
        ? t("productItems.menu.emptyTitle")
        : t("productItems.service.emptyTitle"),
      emptySubtitle: isMenu
        ? t("productItems.menu.emptySubtitle")
        : t("productItems.service.emptySubtitle"),
      addButtonText: isMenu
        ? t("productItems.menu.addButton")
        : t("productItems.service.addButton"),
      deleteSuccessText: isMenu
        ? t("productItems.menu.deleteSuccess")
        : t("productItems.service.deleteSuccess"),
    }),
    [isMenu, t],
  );

  // Fetch items
  const { data: items = [], isLoading } = useQuery({
    queryKey: [queryKey, activityId],
    queryFn: () => getAllFn(activityId),
    enabled: !!activityId,
  });

  // Sort items by display_order
  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.display_order - b.display_order),
    [items],
  );
  const serviceCategoryCounts = useMemo(() => {
    if (!groupsByServiceCategory) return {} as Record<string, number>;

    return sortedItems.reduce(
      (acc, item) => {
        const key =
          "service_category_id" in item && item.service_category_id
            ? item.service_category_id
            : "UNCATEGORIZED";
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [groupsByServiceCategory, sortedItems]);
  const filteredServiceItems = useMemo(() => {
    if (!groupsByServiceCategory || selectedCategory === "ALL")
      return sortedItems;

    return sortedItems.filter((item) => {
      if (!("service_category_id" in item)) return false;
      const categoryKey = item.service_category_id || "UNCATEGORIZED";
      return categoryKey === selectedCategory;
    });
  }, [groupsByServiceCategory, selectedCategory, sortedItems]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (itemId: string) => deleteFn(activityId, itemId),
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: [queryKey, activityId] });
      setPendingDelete(null);
      toast.success(theme.deleteSuccessText);
    },
    onError: (err) => {
      toastApiError(
        err,
        "generic",
        isMenu
          ? t("productItems.toastDeleteErrorMenu")
          : t("productItems.toastDeleteErrorService"),
      );
    },
  });

  const toggleVisibleMutation = useMutation({
    mutationFn: ({
      itemId,
      isVisible,
    }: {
      itemId: string;
      isVisible: boolean;
    }) => updateFn(activityId, itemId, { is_visible: isVisible }),
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: [queryKey, activityId] });
    },
    onError: (err) => {
      toastApiError(
        err,
        "generic",
        isMenu
          ? t("productItems.toastUpdateErrorMenu")
          : t("productItems.toastUpdateErrorService"),
      );
    },
  });

  // Reorder mutation
  const reorderMutation = useMutation({
    mutationFn: async (updates: { id: string; display_order: number }[]) => {
      await Promise.all(
        updates.map(({ id, display_order }) =>
          updateFn(activityId, id, { display_order }),
        ),
      );
    },
    onSuccess: () => {
      queryClientHook.invalidateQueries({ queryKey: [queryKey, activityId] });
    },
    onError: (err) => {
      toastApiError(err, "generic", t("productItems.toastReorderError"));
      queryClientHook.invalidateQueries({ queryKey: [queryKey, activityId] });
    },
  });

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: ProductItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = (item: ProductItem) => {
    setPendingDelete(item);
  };

  const handleToggleVisible = (item: ProductItem) => {
    toggleVisibleMutation.mutate({
      itemId: item.id,
      isVisible: !item.is_visible,
    });
  };

  const handleDragEnd = (event: DragEndEvent, itemsInGroup: ProductItem[]) => {
    if (groupsByServiceCategory && selectedCategory !== "ALL") return;

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const reordered = reorderByDragEnd(itemsInGroup, event, (item) => item.id);
    if (!reordered) return;

    // Calculate new display_order values
    const updates = reordered.map((item, index) => ({
      id: item.id,
      display_order: index,
    }));

    // Optimistic update
    queryClientHook.setQueryData<ProductItem[]>(
      [queryKey, activityId],
      (old) => {
        if (!old) return old;
        return old.map((item) => {
          const update = updates.find((u) => u.id === item.id);
          return update
            ? { ...item, display_order: update.display_order }
            : item;
        });
      },
    );

    // Persist to server
    reorderMutation.mutate(updates);
  };

  // Group items by fixed menu course (legacy restaurants without freeform labels)
  const groupedItems = useMemo(() => {
    if (groupsByServiceCategory) return {} as Record<MenuCategory, ProductItem[]>;

    return MENU_CATEGORIES.reduce(
      (acc, category) => {
        acc[category.value] = sortedItems.filter(
          (item) =>
            "menu_category" in item && item.menu_category === category.value,
        );
        return acc;
      },
      {} as Record<MenuCategory, ProductItem[]>,
    );
  }, [groupsByServiceCategory, sortedItems]);

  const uncategorizedMenuItems = useMemo(() => {
    if (groupsByServiceCategory) return [];
    return sortedItems.filter(
      (item) => !item.menu_category && !item.service_category_id,
    );
  }, [groupsByServiceCategory, sortedItems]);

  const customMenuCategoryGroups = useMemo(() => {
    if (groupsByServiceCategory) {
      return [] as { category: ServiceCategory; items: ProductItem[] }[];
    }
    return sortedServiceCategories
      .map((category) => ({
        category,
        items: sortedItems.filter(
          (item) =>
            !item.menu_category && item.service_category_id === category.id,
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [groupsByServiceCategory, sortedItems, sortedServiceCategories]);

  const filteredCategories =
    selectedCategory === "ALL"
      ? MENU_CATEGORIES.filter((cat) => groupedItems[cat.value]?.length > 0)
      : MENU_CATEGORIES.filter((cat) => cat.value === selectedCategory);

  const IconComponent = theme.Icon;

  const renderSortableList = (itemsToRender: ProductItem[]) => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={(event) => handleDragEnd(event, itemsToRender)}
    >
      <SortableContext
        items={itemsToRender.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2">
          {itemsToRender.map((item) => {
            const serviceCategory =
              groupsByServiceCategory && "service_category_id" in item
                ? sortedServiceCategories.find(
                    (category) => category.id === item.service_category_id,
                  )
                : undefined;

            const serviceCategoryName = serviceCategory
              ? resolveCatalogTranslation(
                  serviceCategory.name_translations,
                  i18n.language,
                )
              : undefined;

            return (
              <SortableItem
                key={item.id}
                item={{
                  ...item,
                  serviceCategoryName,
                }}
                priceColorClass={theme.priceColor}
                surfaceClassName={theme.itemBgColor}
                onEdit={() => handleEdit(item)}
                onDelete={() => handleDelete(item)}
                onToggleVisible={() => handleToggleVisible(item)}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  return (
    <>
      <SectionCard
        data-locale={i18n.language}
        title={theme.title}
        icon={IconComponent}
        count={items.length}
        action={
          <Button
            type="button"
            size="sm"
            variant={theme.buttonVariant}
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={handleAdd}
          >
            {theme.addButtonText}
          </Button>
        }
        className="mb-6"
        contentClassName="space-y-0"
      >
        {!groupsByServiceCategory && items.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            <Chip
              key="all"
              size="sm"
              selected={selectedCategory === "ALL"}
              onClick={() => setSelectedCategory("ALL")}
            >
              {t("productItems.filterAll", { count: items.length })}
            </Chip>
            {MENU_CATEGORIES.filter(
              (cat) => groupedItems[cat.value]?.length > 0,
            ).map((cat) => (
              <Chip
                key={cat.value}
                size="sm"
                selected={selectedCategory === cat.value}
                onClick={() => setSelectedCategory(cat.value)}
              >
                {`${menuCategoryDisplayLabel(cat.value)} (${groupedItems[cat.value].length})`}
              </Chip>
            ))}
            {customMenuCategoryGroups.map(({ category, items: groupItems }) => (
              <Chip
                key={category.id}
                size="sm"
                selected={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {`${resolveCatalogTranslation(
                  category.name_translations,
                  i18n.language,
                )} (${groupItems.length})`}
              </Chip>
            ))}
            {uncategorizedMenuItems.length > 0 && (
              <Chip
                size="sm"
                selected={selectedCategory === "UNCATEGORIZED"}
                onClick={() => setSelectedCategory("UNCATEGORIZED")}
              >
                {t("productItems.uncategorized", {
                  count: uncategorizedMenuItems.length,
                })}
              </Chip>
            )}
          </div>
        )}
        {groupsByServiceCategory &&
          items.length > 0 &&
          Object.keys(serviceCategoryCounts).length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              <Chip
                key="all"
                size="sm"
                selected={selectedCategory === "ALL"}
                onClick={() => setSelectedCategory("ALL")}
              >
                {t("productItems.filterAll", { count: items.length })}
              </Chip>
              {sortedServiceCategories
                .filter((category) => serviceCategoryCounts[category.id] > 0)
                .map((category) => (
                  <Chip
                    key={category.id}
                    size="sm"
                    selected={selectedCategory === category.id}
                    onClick={() => setSelectedCategory(category.id)}
                  >
                    {resolveCatalogTranslation(
                      category.name_translations,
                      i18n.language,
                    )}{" "}
                    ({serviceCategoryCounts[category.id]})
                  </Chip>
                ))}
              {(serviceCategoryCounts.UNCATEGORIZED || 0) > 0 && (
                <Chip
                  size="sm"
                  selected={selectedCategory === "UNCATEGORIZED"}
                  onClick={() => setSelectedCategory("UNCATEGORIZED")}
                >
                  {t("productItems.uncategorized", {
                    count: serviceCategoryCounts.UNCATEGORIZED,
                  })}
                </Chip>
              )}
            </div>
          )}

        {isLoading ? (
          <LoadingState className="py-8" />
        ) : items.length === 0 ? (
          <EmptyState
            variant="dashed"
            size="md"
            icon={IconComponent}
            title={theme.emptyTitle}
            description={theme.emptySubtitle}
          />
        ) : !groupsByServiceCategory ? (
          <div className="space-y-5">
            {(selectedCategory === "ALL" ||
              selectedCategory === "UNCATEGORIZED") &&
              uncategorizedMenuItems.length > 0 && (
                <div>
                  <h4 className="mb-2 text-xs font-medium text-surface-500">
                    {t("productItem.noCategory")}
                  </h4>
                  {renderSortableList(uncategorizedMenuItems)}
                </div>
              )}
            {selectedCategory !== "UNCATEGORIZED" &&
              filteredCategories.map((category) => (
                <div key={category.value}>
                  <h4 className="mb-2 text-xs font-medium text-surface-500">
                    {menuCategoryDisplayLabel(category.value)}
                  </h4>
                  {renderSortableList(groupedItems[category.value])}
                </div>
              ))}
            {selectedCategory !== "UNCATEGORIZED" &&
              customMenuCategoryGroups
                .filter(
                  (group) =>
                    selectedCategory === "ALL" ||
                    group.category.id === selectedCategory,
                )
                .map(({ category, items: groupItems }) => (
                  <div key={category.id}>
                    <h4 className="mb-2 text-xs font-medium text-surface-500">
                      {resolveCatalogTranslation(
                        category.name_translations,
                        i18n.language,
                      )}
                    </h4>
                    {renderSortableList(groupItems)}
                  </div>
                ))}
          </div>
        ) : (
          renderSortableList(filteredServiceItems)
        )}
      </SectionCard>

      <ProductItemModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        activityId={activityId}
        editingItem={editingItem}
        itemType={itemType}
        entityType={entityType}
        queryKey={queryKey}
        createFn={createFn}
        updateFn={updateFn}
        serviceCategories={sortedServiceCategories}
        activityCommissionRate={activityCommissionRate}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
       
        isOpen={pendingDelete != null}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        title={tCommon("delete")}
        message={t("productItems.confirmDelete", {
          name: pendingDelete
            ? resolveCatalogTranslation(
                pendingDelete.name_translations,
                i18n.language,
                t("productItems.thisItem"),
              )
            : t("productItems.thisItem"),
        })}
        confirmText={tCommon("delete")}
        isDestructive
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
